import { StoreState } from '@/store';
import { parseISOString } from '@/utils/parseISOString';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { delay, map, take } from 'rxjs/operators';
import {
  DetailToFilterFromBack, GetReportInfringements, GetReportLicense,
  GetReportRequirements, ResponseFilterInfringements, ResponseFilterInfringementsFromBack,
  ResponseFilterLicense, ResponseFilterLicenseFromBack, ResponseFilterRequirements,
  ResponseFilterRequirementsFromBack, ResponseInfringementsExcel,
  ResponseInfringementsExcelFromBack, ResponseLicenseExcel, ResponseLicenseExcelFromBack,
  ResponseRequirementExcel, ResponseRequirementExcelFromBack, SelectedListToFilter,
} from '../../interfaces/reports/reports';
import { normalize } from '@/utils/normalizeString';

@Injectable({
  providedIn: 'root',
})
export class ReportsService {

  constructor(
    private http: HttpClient,
    private store: Store<StoreState>,
  ) { }

  getStateLicenseToFilter(): Observable<SelectedListToFilter[]> {
    return this.http.get<DetailToFilterFromBack[]>('/Common/LicenseStatus').pipe(
      map((detailFromBack) => detailFromBack
        .map(({ id, name }) => ({
          id, name: normalize(name) === normalize('Próximo a Vencer') ? 'Vigente' : name, isActive: false,
        }))),
    );
  }

  getTypesRequest(): Observable<SelectedListToFilter[]> {
    return this.http.get<DetailToFilterFromBack[]>('/Common/RequirementType').pipe(
      map((detailFromBack) => detailFromBack
        .map(({ id, name }) => ({
          id, name, isActive: false,
        }))),
    );

  }
  getStateRequest(): Observable<SelectedListToFilter[]> {
    return this.http.get<DetailToFilterFromBack[]>('/Common/RequirementStatus').pipe(
      map((detailFromBack) => detailFromBack
        .map(({ id, name }) => ({
          id, name, isActive: false,
        }))),
    );
  }

  getReportLicense(formRequest: GetReportLicense): Observable<ResponseFilterLicense[]> {
    const cleanedFormRequest = {
      ...formRequest,
      licensesStatus: formRequest.licensesStatus.reduce<{ id: number }[]>((vector, licenseStatus) => {
        const idString = <unknown>licenseStatus.id as string;
        idString.split(',').map((id) => {
          vector.push({ id: +id });
        });
        return vector;
      }, []),
      zones: formRequest.zones.reduce<{ id: number }[]>((vector, zone) => {
        const idString = <unknown>zone.id as string;
        idString.split(',').map((id) => {
          vector.push({ id: +id });
        });
        return vector;
      }, []),
    };

    return this.http.post<ResponseFilterLicenseFromBack[]>('/Report/GetLicenseReportView', cleanedFormRequest).pipe(take(1))
      .pipe(
        map((response) => response.map((data) => ({
          ...data,
          expirationDate: parseISOString(data.expirationDate),
        }))),
        take(1),
      );
  }

  getReportInfringements(formRequest: GetReportInfringements): Observable<ResponseFilterInfringements[]> {
    const cleanedFormRequest = {
      ...formRequest,
      zones: formRequest.zones.reduce<{ id: number }[]>((vector, zone) => {
        const idString = <unknown>zone.id as string;
        idString.split(',').map((id) => {
          vector.push({ id: +id });
        });
        return vector;
      }, []),
      infringementsTypes: formRequest.infringementsTypes.reduce<{ id: number }[]>((vector, severity) => {
        const idString = <unknown>severity.id as string;
        idString.split(',').map((id) => {
          vector.push({ id: +id });
        });
        return vector;
      }, []),
      infringementsSanctions: formRequest.infringementsSanctions.reduce<{ id: number }[]>((vector, sanction) => {
        const idString = <unknown>sanction.id as string;
        idString.split(',').map((id) => {
          vector.push({ id: +id });
        });
        return vector;
      }, []),
    };
    return this.http.post<ResponseFilterInfringementsFromBack[]>('/Report/GetInfringementReportView', cleanedFormRequest).pipe(take(1))
      .pipe(
        map((response) => response.map((data) => ({
          ...data,
          dateOfIssue: parseISOString(data.dateOfIssue),
        }))),
        take(1),
      );
  }

  getReportRequirements(formRequest: GetReportRequirements): Observable<ResponseFilterRequirements[]> {
    return this.http.post<ResponseFilterRequirementsFromBack[]>('/Report/GetRequestReportView', formRequest).pipe(take(1))
      .pipe(
        map((response) => response.map((data) => ({
          ...data,
          requestDate: parseISOString(data.requestDate),
        }))),
        take(1),
      );
  }

  getReportLicenseToExcel(formRequest: GetReportLicense): Observable<ResponseLicenseExcel[]> {

    const cleanedFormRequest = {
      ...formRequest,
      licensesStatus: formRequest.licensesStatus.reduce<{ id: number }[]>((vector, licenseStatus) => {
        const idString = <unknown>licenseStatus.id as string;
        idString.split(',').map((id) => {
          vector.push({ id: +id });
        });
        return vector;
      }, []),
      zones: formRequest.zones.reduce<{ id: number }[]>((vector, zone) => {
        const idString = <unknown>zone.id as string;
        idString.split(',').map((id) => {
          vector.push({ id: +id });
        });
        return vector;
      }, []),
    };

    return this.http.post<ResponseLicenseExcelFromBack[]>('/Report/GetLicenseReportXlsx', cleanedFormRequest).pipe(take(1))
      .pipe(
        map((response) => response.map((data) => ({
          ...data,
          licenseExpirationDate: data.licenseExpirationDate ? parseISOString(data.licenseExpirationDate) : undefined,
          requestDate: data.requestDate ? parseISOString(data.requestDate) : undefined,
          manualSuspensionDate: data.manualSuspensionDate ? parseISOString(data.manualSuspensionDate) : undefined,
          manualSuspensionDateFrom: data.manualSuspensionDateFrom ? parseISOString(data.manualSuspensionDateFrom) : undefined,
          manualSuspensionDateTo: data.manualSuspensionDateTo ? parseISOString(data.manualSuspensionDateTo) : undefined,
        }))),
        take(1),
      );
  }

  getReportInfringementsToExcel(formRequest: GetReportInfringements): Observable<ResponseInfringementsExcel[]> {
    const cleanedFormRequest = {
      ...formRequest,
      zones: formRequest.zones.reduce<{ id: number }[]>((vector, zone) => {
        const idString = <unknown>zone.id as string;
        idString.split(',').map((id) => {
          vector.push({ id: +id });
        });
        return vector;
      }, []),
      infringementsTypes: formRequest.infringementsTypes.reduce<{ id: number }[]>((vector, severity) => {
        const idString = <unknown>severity.id as string;
        idString.split(',').map((id) => {
          vector.push({ id: +id });
        });
        return vector;
      }, []),
      infringementsSanctions: formRequest.infringementsSanctions.reduce<{ id: number }[]>((vector, sanction) => {
        const idString = <unknown>sanction.id as string;
        idString.split(',').map((id) => {
          vector.push({ id: +id });
        });
        return vector;
      }, []),
    };

    return this.http.post<ResponseInfringementsExcelFromBack[]>('/Report/GetInfringementReportXlsx', cleanedFormRequest).pipe(take(1))
      .pipe(
        map((response) => response.map((data) => {
          const dateTimeCorrected = data.infringementDateTime ? parseISOString(data.infringementDateTime, true) : undefined;
          if (dateTimeCorrected) {
            dateTimeCorrected.setHours(dateTimeCorrected?.getHours() + (dateTimeCorrected.getTimezoneOffset() / 60));
          }
          return ({
            ...data,
            infringementDateTime: dateTimeCorrected,
            sanctionStart: data.sanctionStart ? parseISOString(data.sanctionStart) : undefined,
            sanctionEnd: data.sanctionEnd ? parseISOString(data.sanctionEnd) : undefined,
          });

        })),
        take(1),
      );
  }

  getReportRequirementsToExcel(formRequest: GetReportRequirements): Observable<ResponseRequirementExcel[]> {
    return this.http.post<ResponseRequirementExcelFromBack[]>('/Report/GetRequestReportXlsx', formRequest).pipe(take(1))
      .pipe(
        map((response) => response.map((data) => ({
          ...data,
          requestDate: data.requestDate ? parseISOString(data.requestDate) : undefined,
          lastApprovalDate: data.requestDate ? parseISOString(data.requestDate) : undefined,
          licenseExpirationDate: data.requestDate ? parseISOString(data.requestDate) : undefined,
        }))),
        take(1),
      );
  }

}
